'use client';

import React, { ReactNode, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { Box, Spinner, Alert, AlertIcon, Button, VStack, Heading } from '@chakra-ui/react';

interface ProtectedPageProps {
    children: ReactNode;
    permission?: string;
    role?: UserRole;
    roles?: UserRole[];
    redirectTo?: string;
    showUnauthorized?: boolean;
}

export function ProtectedPage({
    children,
    permission,
    role,
    roles,
    redirectTo = '/login',
    showUnauthorized = true
}: ProtectedPageProps) {
    const { user, hasPermission, isRole, loading } = useAuth();
    const router = useRouter();

    useEffect(() => {
        if (!loading && !user) {
            router.push(redirectTo);
        }
    }, [user, loading, router, redirectTo]);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Box>Loading...</Box>
                </VStack>
            </Box>
        );
    }

    if (!user) {
        return null; // Will redirect via useEffect
    }

    // Check permission-based access
    if (permission && !hasPermission(permission)) {
        if (!showUnauthorized) {
            router.push('/dashboard');
            return null;
        }

        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                p={4}
            >
                <VStack spacing={6} maxW="md" textAlign="center">
                    <Heading size="lg" color="red.500">Access Denied</Heading>
                    <Alert status="error">
                        <AlertIcon />
                        You don't have permission to access this page.
                    </Alert>
                    <Button
                        colorScheme="blue"
                        onClick={() => router.push('/dashboard')}
                    >
                        Go to Dashboard
                    </Button>
                </VStack>
            </Box>
        );
    }

    // Check single role-based access
    if (role && !isRole(role)) {
        if (!showUnauthorized) {
            router.push('/dashboard');
            return null;
        }

        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                p={4}
            >
                <VStack spacing={6} maxW="md" textAlign="center">
                    <Heading size="lg" color="red.500">Access Denied</Heading>
                    <Alert status="error">
                        <AlertIcon />
                        You don't have the required role to access this page.
                    </Alert>
                    <Button
                        colorScheme="blue"
                        onClick={() => router.push('/dashboard')}
                    >
                        Go to Dashboard
                    </Button>
                </VStack>
            </Box>
        );
    }

    // Check multiple roles-based access
    if (roles && roles.length > 0 && !roles.some(r => isRole(r))) {
        if (!showUnauthorized) {
            router.push('/dashboard');
            return null;
        }

        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                p={4}
            >
                <VStack spacing={6} maxW="md" textAlign="center">
                    <Heading size="lg" color="red.500">Access Denied</Heading>
                    <Alert status="error">
                        <AlertIcon />
                        You don't have the required role to access this page.
                    </Alert>
                    <Button
                        colorScheme="blue"
                        onClick={() => router.push('/dashboard')}
                    >
                        Go to Dashboard
                    </Button>
                </VStack>
            </Box>
        );
    }

    return <>{children}</>;
} 